/**
 * Royalties — onglet dédié dans KPI & Transactions.
 *
 * Deux sous-onglets :
 *   • Bénéficiaires Royalties : CRUD contact → taux × départements + dates
 *   • Relevés Royalties       : calcul mensuel automatique (miroir Bulletins)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Users, FileText, CalendarDays, Percent, Download,
  ChevronRight, CheckCircle2, RotateCcw, Save, X, ShieldCheck, AlertCircle,
  Building2, ChevronLeft, Search, KeyRound, MoreHorizontal, Copy, Power,
  UserCog, UserX, Vault, ArrowUpDown, ArrowUpCircle, ArrowDownCircle,
  BanknoteArrowDown,
} from "lucide-react";

import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { format as formatDate, parseISO as parseIsoDate } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import useIsMobile from "../../hooks/useIsMobile";
import SearchableSelect from "../SearchableSelect";

/* ═══════════ Helpers ═══════════ */
const money = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" })
    .format(Number(v || 0));

const todayIso = () => new Date().toISOString().slice(0, 10);

// Catégories de partenariat pour un bénéficiaire Royalties (miroir backend).
const ROYALTY_PARTNERSHIP_CATEGORIES = [
  { value: "ambassadeur", label: "Ambassadeur(rice) de Marque" },
  { value: "autre", label: "Autre partenariat" },
];
const royaltyPartnershipLabel = (v) =>
  ROYALTY_PARTNERSHIP_CATEGORIES.find((c) => c.value === v)?.label || "—";
const currentMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return "—";
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const frDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const slugify = (s) =>
  (s || "beneficiaire").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* ═══════════ Composant racine ═══════════ */
export default function RoyaltiesSection({ isAdmin }) {
  const [subtab, setSubtab] = useState("beneficiaries");

  return (
    <div className="space-y-4" data-testid="royalties-section">
      <Tabs value={subtab} onValueChange={setSubtab}>
        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="rounded-sm w-max">
            <TabsTrigger
              value="beneficiaries"
              data-testid="royalties-subtab-beneficiaries"
              className="inline-flex items-center gap-1.5"
            >
              <Users size={13} /> Bénéficiaires Royalties
            </TabsTrigger>
            <TabsTrigger
              value="statements"
              data-testid="royalties-subtab-statements"
              className="inline-flex items-center gap-1.5"
            >
              <FileText size={13} /> Relevés Royalties
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="beneficiaries" className="mt-4">
          <BeneficiariesPanel isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="statements" className="mt-4">
          <StatementsPanel isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ═══════════ Panel 1 — Bénéficiaires ═══════════ */
function BeneficiariesPanel({ isAdmin }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Portail partenaire (activation / reset / suspend / delete)
  const [portalCredentials, setPortalCredentials] = useState(null); // {email, temporary_password, name}
  const [activatingId, setActivatingId] = useState(null);
  const [emailPromptTarget, setEmailPromptTarget] = useState(null); // {row, email}
  const [portalDeleteTarget, setPortalDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/royalty-beneficiaries");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/royalty-beneficiaries/${deleteTarget.id}`);
      toast.success("Bénéficiaire supprimé");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // ─── Actions Portail Partenaire ───────────────────────────────────
  const doActivate = async (row, emailOverride) => {
    setActivatingId(row.id);
    try {
      const { data } = await api.post(
        `/royalty-beneficiaries/${row.id}/activate-portal`,
        { email: emailOverride || null },
      );
      setPortalCredentials({
        title: "Espace partenaire activé",
        description: "Transmettez ces identifiants au partenaire. Le mot de passe temporaire ne sera plus visible ensuite.",
        email: data.email,
        temporary_password: data.temporary_password,
        name: data.name,
      });
      setEmailPromptTarget(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur";
      // Si l'email est manquant, on ouvre le prompt
      if (typeof detail === "string" && detail.toLowerCase().includes("aucun email")) {
        setEmailPromptTarget({ row, email: "" });
      } else {
        toast.error(detail);
      }
    } finally {
      setActivatingId(null);
    }
  };

  const doResetPassword = async (row) => {
    try {
      const { data } = await api.post(`/royalty-beneficiaries/${row.id}/reset-portal-password`);
      setPortalCredentials({
        title: "Mot de passe régénéré",
        description: "Le partenaire devra le changer à sa prochaine connexion.",
        email: data.email,
        temporary_password: data.temporary_password,
        name: data.name,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doTogglePortal = async (row, is_active) => {
    try {
      await api.patch(`/royalty-beneficiaries/${row.id}/portal`, { is_active });
      toast.success(is_active ? "Espace réactivé" : "Espace suspendu");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doDeletePortal = async () => {
    if (!portalDeleteTarget) return;
    try {
      await api.delete(`/royalty-beneficiaries/${portalDeleteTarget.id}/portal`);
      toast.success("Espace partenaire supprimé");
      setPortalDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-4" data-testid="royalty-beneficiaries-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
            Bénéficiaires
          </p>
          <h2 className="text-lg font-semibold text-zinc-900">
            Contacts rémunérés par Royalties
          </h2>
        </div>
        {isAdmin && (
          <Button
            onClick={openCreate}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm"
            data-testid="new-royalty-beneficiary-btn"
          >
            <Plus size={14} className="mr-1" /> Nouveau bénéficiaire
          </Button>
        )}
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Bénéficiaire</th>
                <th className="text-left px-4 py-2.5">ID Partenaire</th>
                <th className="text-left px-4 py-2.5">Départements</th>
                <th className="text-right px-4 py-2.5">Taux</th>
                <th className="text-left px-4 py-2.5">Début</th>
                <th className="text-left px-4 py-2.5">Fin</th>
                <th className="text-left px-4 py-2.5">Espace</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-zinc-400 font-mono">Chargement…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-xs text-zinc-400">
                    Aucun bénéficiaire Royalties enregistré.
                  </td>
                </tr>
              ) : (
                items.map((it) => {
                  const portal = it.portal || {};
                  return (
                  <tr
                    key={it.id}
                    className="border-t border-zinc-100 hover:bg-zinc-50"
                    data-testid={`royalty-beneficiary-row-${it.id}`}
                  >
                    <td className="px-4 py-2.5 font-medium text-zinc-900">{it.contact_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-purple-700" data-testid={`royalty-partner-id-${it.id}`}>
                      {it.partner_id || "—"}
                      {it.partnership_category && (
                        <div className="mt-0.5 text-[10px] text-zinc-500 font-sans normal-case tracking-normal">
                          {royaltyPartnershipLabel(it.partnership_category)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(it.brands || []).map((b) => (
                          <Badge
                            key={b.id}
                            variant="outline"
                            className="text-[10px]"
                            style={{ borderColor: b.color || "#e4e4e7", color: b.color || "#3f3f46" }}
                          >
                            {b.name}
                          </Badge>
                        ))}
                        {(it.brands || []).length === 0 && <span className="text-zinc-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">
                      {Number(it.rate || 0).toFixed(2).replace(".", ",")}&nbsp;%
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{frDate(it.start_date)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{it.end_date ? frDate(it.end_date) : "∞"}</td>
                    <td className="px-4 py-2.5">
                      {!portal.enabled ? (
                        <Badge variant="outline" className="text-[10px] text-zinc-500" data-testid={`portal-status-${it.id}`}>
                          Non activé
                        </Badge>
                      ) : portal.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]" data-testid={`portal-status-${it.id}`}>
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px]" data-testid={`portal-status-${it.id}`}>
                          Suspendu
                        </Badge>
                      )}
                      {portal.email && (
                        <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate max-w-[180px]" title={portal.email}>
                          {portal.email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        {/* Coffre — accès direct au tableau de bord partenaire (admin/manager) */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/consulter-partenaire/${it.id}`)}
                          data-testid={`open-partner-space-${it.id}`}
                          title="Ouvrir l'espace personnel de ce partenaire (mode consultation)"
                          className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800 hover:border-purple-300"
                        >
                          <Vault size={13} />
                        </Button>
                        {isAdmin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(it)}
                            data-testid={`edit-royalty-beneficiary-${it.id}`}
                          >
                            <Pencil size={11} className="mr-1" /> Modifier
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`portal-menu-${it.id}`}
                                disabled={activatingId === it.id}
                              >
                                <UserCog size={12} className="mr-1" /> Espace
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                                Espace partenaire
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {!portal.enabled ? (
                                <DropdownMenuItem
                                  onClick={() => doActivate(it)}
                                  data-testid={`portal-activate-${it.id}`}
                                >
                                  <KeyRound size={12} className="mr-2" /> Activer l'espace
                                </DropdownMenuItem>
                              ) : (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => doResetPassword(it)}
                                    data-testid={`portal-reset-${it.id}`}
                                  >
                                    <RotateCcw size={12} className="mr-2" /> Réinitialiser le MDP
                                  </DropdownMenuItem>
                                  {portal.is_active ? (
                                    <DropdownMenuItem
                                      onClick={() => doTogglePortal(it, false)}
                                      data-testid={`portal-suspend-${it.id}`}
                                    >
                                      <Power size={12} className="mr-2" /> Suspendre l'espace
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onClick={() => doTogglePortal(it, true)}
                                      data-testid={`portal-reactivate-${it.id}`}
                                    >
                                      <Power size={12} className="mr-2" /> Réactiver l'espace
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                    onClick={() => setPortalDeleteTarget(it)}
                                    data-testid={`portal-delete-${it.id}`}
                                  >
                                    <UserX size={12} className="mr-2" /> Supprimer l'espace
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteTarget(it)}
                            data-testid={`delete-royalty-beneficiary-${it.id}`}
                          >
                            <Trash2 size={11} />
                          </Button>
                        </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BeneficiaryDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        editing={editing}
        onSaved={(res) => {
          setDialogOpen(false);
          setEditing(null);
          load();
          if (res?.portal_credentials?.temporary_password) {
            setPortalCredentials({
              title: "Espace partenaire auto-activé",
              description: "Le compte a été créé automatiquement à la validation du bénéficiaire. Transmettez ces identifiants — le mot de passe temporaire ne sera plus visible ensuite.",
              email: res.portal_credentials.email,
              temporary_password: res.portal_credentials.temporary_password,
              name: res.portal_credentials.name,
            });
          }
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-royalty-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce bénéficiaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les relevés Royalties associés (draft et validés) seront également supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-royalty-confirm-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Prompt email : quand le contact n'a pas d'email enregistré */}
      <Dialog
        open={!!emailPromptTarget}
        onOpenChange={(v) => !v && setEmailPromptTarget(null)}
      >
        <DialogContent data-testid="portal-email-prompt">
          <DialogHeader>
            <DialogTitle>Adresse email du partenaire</DialogTitle>
            <DialogDescription>
              Aucun email n'est enregistré pour ce contact. Précisez l'adresse à utiliser
              pour l'activation de l'espace partenaire.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="portal-email">Email</Label>
            <Input
              id="portal-email"
              type="email"
              value={emailPromptTarget?.email || ""}
              onChange={(e) =>
                setEmailPromptTarget((s) => (s ? { ...s, email: e.target.value } : s))
              }
              data-testid="portal-email-prompt-input"
              placeholder="partenaire@exemple.fr"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPromptTarget(null)}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                emailPromptTarget?.email &&
                doActivate(emailPromptTarget.row, emailPromptTarget.email.trim())
              }
              disabled={!emailPromptTarget?.email}
              data-testid="portal-email-prompt-confirm"
            >
              <KeyRound size={13} className="mr-1.5" /> Activer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression espace partenaire */}
      <AlertDialog
        open={!!portalDeleteTarget}
        onOpenChange={(v) => !v && setPortalDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="portal-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'espace partenaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte utilisateur du partenaire sera supprimé et il ne pourra plus se
              connecter. Le bénéficiaire Royalties et ses relevés sont conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeletePortal}
              className="bg-red-600 hover:bg-red-700"
              data-testid="portal-delete-confirm-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Affichage identifiants générés (à copier & transmettre) */}
      <Dialog
        open={!!portalCredentials}
        onOpenChange={(v) => !v && setPortalCredentials(null)}
      >
        <DialogContent data-testid="portal-credentials-dialog">
          <DialogHeader>
            <DialogTitle>{portalCredentials?.title || "Identifiants générés"}</DialogTitle>
            <DialogDescription>{portalCredentials?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Email</p>
                  <p className="font-mono text-sm" data-testid="portal-credentials-email">
                    {portalCredentials?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(portalCredentials?.email || "");
                    toast.success("Email copié");
                  }}
                >
                  <Copy size={13} />
                </Button>
              </div>
            </div>
            <div className="border border-purple-200 rounded-sm p-3 bg-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-purple-700 font-mono">
                    Mot de passe temporaire
                  </p>
                  <p className="font-mono text-sm font-bold text-purple-900" data-testid="portal-credentials-password">
                    {portalCredentials?.temporary_password}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(portalCredentials?.temporary_password || "");
                    toast.success("Mot de passe copié");
                  }}
                  data-testid="portal-credentials-copy-password"
                >
                  <Copy size={13} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              <AlertCircle size={12} className="inline mr-1" />
              Ce mot de passe ne sera plus affiché. Copiez-le maintenant.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setPortalCredentials(null)}
              data-testid="portal-credentials-close"
            >
              J'ai copié les identifiants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════ Dialog création / édition ═══════════ */
function BeneficiaryDialog({ open, onClose, editing, onSaved }) {
  const [contacts, setContacts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_id: "",
    partner_id: "",
    partnership_category: "",
    brand_ids: [],
    rate: 0,
    start_date: todayIso(),
    end_date: "",
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [b, br] = await Promise.all([
          api.get("/beneficiaries"),
          api.get("/brands"),
        ]);
        setContacts(b.data || []);
        setBrands(br.data || []);
      } catch (e) {
        toast.error("Erreur chargement contacts / départements");
      }
    })();
    if (editing) {
      setForm({
        contact_id: editing.contact_id,
        partner_id: editing.partner_id || "",
        partnership_category: editing.partnership_category || "",
        brand_ids: editing.brand_ids || [],
        rate: editing.rate ?? 0,
        start_date: editing.start_date,
        end_date: editing.end_date,
      });
    } else {
      setForm({ contact_id: "", partner_id: "", partnership_category: "", brand_ids: [], rate: 0, start_date: todayIso(), end_date: "" });
    }
  }, [open, editing]);

  // Départements autorisés = ceux du contact sélectionné
  const eligibleBrands = useMemo(() => {
    const c = contacts.find((x) => x.id === form.contact_id);
    if (!c) return [];
    const ids = new Set(c.brand_ids || (c.brand_id ? [c.brand_id] : []));
    return brands.filter((b) => ids.has(b.id));
  }, [form.contact_id, contacts, brands]);

  // Réinitialise brand_ids si le contact change
  useEffect(() => {
    if (!form.contact_id) return;
    const eligibleIds = new Set(eligibleBrands.map((b) => b.id));
    setForm((prev) => ({
      ...prev,
      brand_ids: (prev.brand_ids || []).filter((id) => eligibleIds.has(id)),
    }));
  }, [form.contact_id, eligibleBrands]);

  const toggleBrand = (bid) => {
    setForm((prev) => {
      const set = new Set(prev.brand_ids || []);
      if (set.has(bid)) set.delete(bid); else set.add(bid);
      return { ...prev, brand_ids: Array.from(set) };
    });
  };

  const submit = async () => {
    if (!form.contact_id) return toast.error("Sélectionnez un contact");
    const pid = String(form.partner_id || "").trim().toUpperCase();
    if (!pid || pid.length < 3 || pid.length > 40) {
      return toast.error("ID Partenaire obligatoire (3 à 40 caractères)");
    }
    if (!form.partnership_category) {
      return toast.error("Sélectionnez une catégorie de partenariat");
    }
    if (!form.brand_ids?.length) return toast.error("Sélectionnez au moins un département");
    if (form.rate == null || Number(form.rate) < 0 || Number(form.rate) > 100) {
      return toast.error("Taux invalide (0 à 100)");
    }
    if (!form.start_date) return toast.error("Date de début obligatoire");
    if (form.end_date && form.end_date < form.start_date) {
      return toast.error("La date de fin doit être ≥ date de début");
    }

    setSaving(true);
    try {
      const payload = {
        contact_id: form.contact_id,
        partner_id: pid,
        partnership_category: form.partnership_category,
        brand_ids: form.brand_ids,
        rate: Number(form.rate),
        start_date: form.start_date,
        end_date: form.end_date || null,
      };
      if (editing) {
        await api.put(`/royalty-beneficiaries/${editing.id}`, payload);
        toast.success("Bénéficiaire mis à jour");
        onSaved();
      } else {
        const { data } = await api.post("/royalty-beneficiaries", payload);
        if (data?.portal_credentials?.temporary_password) {
          toast.success("Bénéficiaire créé — espace personnel auto-activé");
        } else {
          toast.success("Bénéficiaire créé");
        }
        onSaved(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="royalty-beneficiary-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Modifier le bénéficiaire" : "Nouveau bénéficiaire Royalties"}
          </DialogTitle>
          <DialogDescription>
            Le taux s&apos;applique au CA net des départements rattachés (identique au tableau de bord KPI).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Contact bénéficiaire *</Label>
            <SearchableSelect
              value={form.contact_id}
              onChange={(v) => setForm((p) => ({ ...p, contact_id: v }))}
              options={contacts.map((c) => ({
                value: c.id,
                label: c.denomination,
                keywords: c.email || c.phone || "",
                hint: c.email || undefined,
              }))}
              placeholder="Sélectionner un contact…"
              searchPlaceholder="Rechercher un contact…"
              disabled={!!editing}
              data-testid="royalty-contact-select"
            />
            {editing && (
              <p className="text-[10px] text-zinc-400 mt-1">
                Le contact ne peut pas être modifié après création.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">ID Partenaire *</Label>
            <Input
              value={form.partner_id}
              onChange={(e) =>
                setForm((p) => ({ ...p, partner_id: e.target.value.toUpperCase() }))
              }
              placeholder="ex : PART-2026-001"
              maxLength={40}
              data-testid="royalty-partner-id-input"
              className="font-mono uppercase"
            />
            <p className="text-[10px] text-zinc-400 mt-1">
              Identifiant unique du partenaire (3 à 40 caractères, sensible à l&apos;unicité).
            </p>
          </div>

          <div>
            <Label className="text-xs">Catégorie partenariat *</Label>
            <select
              value={form.partnership_category}
              onChange={(e) => setForm((p) => ({ ...p, partnership_category: e.target.value }))}
              data-testid="royalty-partnership-category-select"
              className="mt-1 w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
            >
              <option value="">— Sélectionner —</option>
              {ROYALTY_PARTNERSHIP_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-400 mt-1">
              Catégorise la relation partenariale (obligatoire).
            </p>
          </div>

          <div>
            <Label className="text-xs">Départements de rattachement *</Label>
            {!form.contact_id ? (
              <p className="text-xs text-zinc-400 italic py-2">
                Sélectionnez d&apos;abord un contact.
              </p>
            ) : eligibleBrands.length === 0 ? (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-800">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>Ce contact n&apos;est rattaché à aucun département. Modifiez-le dans la page « Contacts &amp; bénéficiaires ».</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 py-1" data-testid="royalty-brands-picker">
                {eligibleBrands.map((b) => {
                  const active = (form.brand_ids || []).includes(b.id);
                  return (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => toggleBrand(b.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                      }`}
                      data-testid={`royalty-brand-toggle-${b.id}`}
                    >
                      {b.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Taux Royalties (%) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.rate}
              onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))}
              data-testid="royalty-rate-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date de début *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                data-testid="royalty-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Date de fin</Label>
              <Input
                type="date"
                value={form.end_date || ""}
                onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                data-testid="royalty-end-date"
                placeholder="Optionnel"
              />
              <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                Laisser vide pour un contrat à durée indéterminée
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="save-royalty-beneficiary-btn"
          >
            <Save size={13} className="mr-1" />
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════ Helpers période (semaine / mois / année) ═══════════ */
// Lundi = 1er jour (ISO). getDay() → 0=dim … 6=sam.
const startOfIsoWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0..6 (0=dim)
  const diff = (day === 0 ? -6 : 1 - day); // lundi
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfIsoWeek = (d) => {
  const s = startOfIsoWeek(d);
  s.setDate(s.getDate() + 6);
  return s;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);
const rangeFor = (mode, anchor = new Date()) => {
  if (mode === "week") return { from: startOfIsoWeek(anchor), to: endOfIsoWeek(anchor) };
  if (mode === "month") return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  if (mode === "year") return { from: startOfYear(anchor), to: endOfYear(anchor) };
  return null;
};
const shiftRange = (mode, current, delta) => {
  const anchor = current?.from ? new Date(current.from) : new Date();
  if (mode === "week") anchor.setDate(anchor.getDate() + delta * 7);
  else if (mode === "month") anchor.setMonth(anchor.getMonth() + delta);
  else if (mode === "year") anchor.setFullYear(anchor.getFullYear() + delta);
  return rangeFor(mode, anchor);
};
const isoDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/* ═══════════ Panel 2 — Relevés ═══════════ */
function StatementsPanel({ isAdmin }) {
  const isMobile = useIsMobile();
  // Onglet actif : week | month | year | custom
  const [periodMode, setPeriodMode] = useState("month");
  // Plage de dates — par défaut : mois en cours
  const [range, setRange] = useState(() => rangeFor("month"));
  // Liste des mois YYYY-MM contenus dans la plage
  const monthsInRange = useMemo(() => {
    if (!range?.from || !range?.to) return [currentMonth()];
    const months = [];
    let d = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    const end = new Date(range.to.getFullYear(), range.to.getMonth(), 1);
    while (d <= end) {
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    return months;
  }, [range]);
  // Mois « primaire » pour PDF/détail (utilisable seulement si plage = 1 mois)
  const singleMonth = monthsInRange.length === 1 ? monthsInRange[0] : null;
  const isSingleMonth = !!singleMonth;

  const [items, setItems] = useState([]);
  const [periodCaNet, setPeriodCaNet] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailBid, setDetailBid] = useState(null);
  const [filters, setFilters] = useState({
    beneficiary_id: "",
    brand_id: "",
    status: "",   // "" | "draft" | "validated"
    q: "",        // recherche libre sur le nom du bénéficiaire
  });

  const load = useCallback(async () => {
    if (monthsInRange.length === 0) { setItems([]); return; }
    setLoading(true);
    try {
      const responses = await Promise.all(
        monthsInRange.map((m) => api.get(`/royalty-statements?month=${m}`))
      );
      // CA net cumulé (période) — vient déjà dédupé du backend : somme des
      // period_ca_net de chaque mois (union des départements des bénéficiaires
      // actifs, sans double comptage).
      const cumulativeNet = responses.reduce(
        (acc, res) => acc + (Number(res.data.period_ca_net) || 0),
        0,
      );
      setPeriodCaNet(cumulativeNet);
      if (monthsInRange.length === 1) {
        setItems(responses[0].data.items || []);
      } else {
        // Agrégation multi-mois par bénéficiaire (avec by_brand cumulé)
        const byBid = new Map();
        for (const res of responses) {
          for (const r of (res.data.items || [])) {
            const key = r.beneficiary_id;
            if (!byBid.has(key)) {
              byBid.set(key, {
                ...r,
                ca_sales: 0,
                ca_refunds: 0,
                ca_net: 0,
                tx_count: 0,
                computed_amount_ht: 0,
                manual_adjustment_ht: null,
                _adjust_total: 0,
                _has_adjust: false,
                amount_ht: 0,
                by_brand: [],
                _by_brand_map: new Map(),
                _statuses: new Set(),
                _months_count: 0,
              });
            }
            const acc = byBid.get(key);
            acc.ca_sales += (r.ca_sales || 0);
            acc.ca_refunds += (r.ca_refunds || 0);
            acc.ca_net += (r.ca_net || 0);
            acc.tx_count += (r.tx_count || 0);
            acc.computed_amount_ht += (r.computed_amount_ht || 0);
            if (r.manual_adjustment_ht != null) {
              acc._has_adjust = true;
              acc._adjust_total += Number(r.manual_adjustment_ht || 0);
            }
            acc.amount_ht += (r.amount_ht || 0);
            for (const br of (r.by_brand || [])) {
              const bk = br.brand_id || "—";
              const row = acc._by_brand_map.get(bk) || {
                brand_id: bk, name: br.name, color: br.color,
                sales: 0, refunds: 0, net: 0, count: 0,
              };
              row.sales += (br.sales || 0);
              row.refunds += (br.refunds || 0);
              row.net += (br.net || 0);
              row.count += (br.count || 0);
              acc._by_brand_map.set(bk, row);
            }
            acc._statuses.add(r.status || "draft");
            acc._months_count += 1;
          }
        }
        const merged = Array.from(byBid.values()).map((r) => ({
          ...r,
          ca_sales: Math.round(r.ca_sales * 100) / 100,
          ca_refunds: Math.round(r.ca_refunds * 100) / 100,
          ca_net: Math.round(r.ca_net * 100) / 100,
          computed_amount_ht: Math.round(r.computed_amount_ht * 100) / 100,
          amount_ht: Math.round(r.amount_ht * 100) / 100,
          manual_adjustment_ht: r._has_adjust ? Math.round(r._adjust_total * 100) / 100 : null,
          by_brand: Array.from(r._by_brand_map.values()).map((b) => ({
            ...b,
            sales: Math.round(b.sales * 100) / 100,
            refunds: Math.round(b.refunds * 100) / 100,
            net: Math.round(b.net * 100) / 100,
          })),
          // « validated » uniquement si TOUS les mois du bénéficiaire dans la plage sont validés
          status: r._statuses.size === 1 && r._statuses.has("validated") ? "validated" : "draft",
        }));
        setItems(merged);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [monthsInRange]);

  useEffect(() => { load(); }, [load]);

  // Options bénéficiaires + départements construites à partir des lignes du mois.
  const beneficiaryOptions = useMemo(() => {
    const seen = new Map();
    for (const r of items) {
      if (!seen.has(r.beneficiary_id)) {
        seen.set(r.beneficiary_id, r.contact_name || "—");
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [items]);

  const brandOptions = useMemo(() => {
    const seen = new Map();
    for (const r of items) {
      for (const b of r.brands || []) {
        if (!seen.has(b.id)) seen.set(b.id, b);
      }
    }
    return Array.from(seen.values()).sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = (filters.q || "").trim().toLowerCase();
    return items.filter((r) => {
      if (filters.beneficiary_id && r.beneficiary_id !== filters.beneficiary_id) return false;
      if (filters.brand_id && !(r.brands || []).some((b) => b.id === filters.brand_id)) return false;
      if (filters.status && (r.status || "draft") !== filters.status) return false;
      if (q && !(r.contact_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filters]);

  const totals = useMemo(() => {
    const totalHt = filteredItems.reduce((s, r) => s + (r.amount_ht || 0), 0);
    // CA net cumulé (période) — Backend dédupe par département (union des
    // départements des bénéficiaires actifs). On ne somme PAS r.ca_net par
    // bénéficiaire pour éviter le double comptage quand plusieurs
    // bénéficiaires partagent une même marque.
    const totalCa = periodCaNet;
    const validated = filteredItems.filter((r) => r.status === "validated").length;
    return { totalHt, totalCa, validated };
  }, [filteredItems, periodCaNet]);

  const activeFiltersCount =
    (filters.beneficiary_id ? 1 : 0) +
    (filters.brand_id ? 1 : 0) +
    (filters.status ? 1 : 0) +
    (filters.q ? 1 : 0);
  const resetFilters = () =>
    setFilters({ beneficiary_id: "", brand_id: "", status: "", q: "" });

  const downloadPdf = async (bid, contactName) => {
    try {
      let url;
      let filename;
      if (isSingleMonth) {
        const res = await api.get(`/royalty-statements/${bid}/${singleMonth}/pdf`, { responseType: "blob" });
        url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        filename = `royalties_${slugify(contactName)}_${singleMonth}.pdf`;
      } else {
        const from = isoDate(range.from);
        const to = isoDate(range.to);
        const res = await api.get(`/royalty-statements-range/${bid}/pdf`, {
          params: { from, to },
          responseType: "blob",
        });
        url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        filename = `royalties_${slugify(contactName)}_${from}_${to}.pdf`;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur téléchargement PDF");
    }
  };

  // Détail : quel que soit le mode (mois unique OU plage agrégée)
  const detail = items.find((r) => r.beneficiary_id === detailBid) || null;

  // Label lisible de la plage sélectionnée
  const rangeLabel = useMemo(() => {
    if (!range?.from) return "Sélectionner une plage";
    const fromStr = formatDate(range.from, "dd MMM yyyy", { locale: frLocale });
    if (!range.to || range.to.toDateString() === range.from.toDateString()) return fromStr;
    const toStr = formatDate(range.to, "dd MMM yyyy", { locale: frLocale });
    return `${fromStr} → ${toStr}`;
  }, [range]);

  const setToCurrentMonth = () => {
    setPeriodMode("month");
    setRange(rangeFor("month"));
  };

  // Onglet cliqué → applique la plage automatiquement autour d'aujourd'hui
  const applyPeriodMode = (mode) => {
    setPeriodMode(mode);
    if (mode !== "custom") setRange(rangeFor(mode));
  };
  const navPeriod = (delta) => {
    if (periodMode === "custom") return;
    setRange((cur) => shiftRange(periodMode, cur, delta));
  };
  const periodLabel = useMemo(() => {
    if (periodMode === "custom" || !range?.from) return null;
    if (periodMode === "week") {
      return `Semaine du ${formatDate(range.from, "dd MMM", { locale: frLocale })} au ${formatDate(range.to, "dd MMM yyyy", { locale: frLocale })}`;
    }
    if (periodMode === "month") {
      return formatDate(range.from, "MMMM yyyy", { locale: frLocale });
    }
    if (periodMode === "year") {
      return `Année ${range.from.getFullYear()}`;
    }
    return null;
  }, [periodMode, range]);

  return (
    <div className="space-y-4" data-testid="royalty-statements-panel">
      {/* Onglets période + navigation */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={periodMode} onValueChange={applyPeriodMode}>
            <TabsList className="rounded-sm" data-testid="royalty-period-tabs">
              <TabsTrigger value="week" data-testid="royalty-period-tab-week" className="text-xs">Semaine</TabsTrigger>
              <TabsTrigger value="month" data-testid="royalty-period-tab-month" className="text-xs">Mois</TabsTrigger>
              <TabsTrigger value="year" data-testid="royalty-period-tab-year" className="text-xs">Année</TabsTrigger>
              <TabsTrigger value="custom" data-testid="royalty-period-tab-custom" className="text-xs">Personnalisé</TabsTrigger>
            </TabsList>
          </Tabs>

          {periodMode !== "custom" && (
            <div className="flex items-center gap-1 border border-zinc-200 rounded-sm bg-white h-8 px-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => navPeriod(-1)}
                aria-label="Période précédente"
                data-testid="royalty-period-prev"
              >
                <ChevronLeft size={14} />
              </Button>
              <span className="text-xs font-mono capitalize px-2 min-w-[160px] text-center" data-testid="royalty-period-label">
                {periodLabel}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => navPeriod(1)}
                aria-label="Période suivante"
                data-testid="royalty-period-next"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          )}

          {periodMode === "custom" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm font-mono text-xs justify-start px-2.5 min-w-[220px] max-w-[300px]"
                  data-testid="royalty-range-btn"
                >
                  <CalendarDays size={13} className="mr-1.5 text-zinc-500 flex-shrink-0" />
                  <span className="truncate">{rangeLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto max-w-[95vw] overflow-auto" align="start" data-testid="royalty-range-popover">
                <Calendar
                  mode="range"
                  numberOfMonths={isMobile ? 1 : 2}
                  locale={frLocale}
                  selected={range}
                  onSelect={(r) => {
                    if (!r) { setRange({ from: undefined, to: undefined }); return; }
                    setRange({
                      from: r.from,
                      to: r.to || r.from,
                    });
                  }}
                  initialFocus
                  data-testid="royalty-range-calendar"
                />
                <div className="flex items-center justify-between gap-2 p-2 border-t border-zinc-200 bg-zinc-50">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={setToCurrentMonth}
                    data-testid="royalty-range-current-month"
                  >
                    Mois en cours
                  </Button>
                  <span className="text-[10px] font-mono text-zinc-500">
                    {monthsInRange.length} mois
                  </span>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {!isSingleMonth && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-mono" data-testid="royalty-aggregated-badge">
              Vue agrégée · {monthsInRange.length} mois
            </Badge>
          )}
        </div>
      </div>

      {/* Filtres — modèle « Commissions partenaires » */}
      <div
        className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap items-end gap-3"
        data-testid="royalty-filters"
      >
        <RoyaltyFilterField label="Bénéficiaire">
          <Select
            value={filters.beneficiary_id || "all"}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, beneficiary_id: v === "all" ? "" : v }))
            }
          >
            <SelectTrigger className="h-8 text-xs w-[200px]" data-testid="royalty-filter-beneficiary">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {beneficiaryOptions.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </RoyaltyFilterField>
        <RoyaltyFilterField label="Département">
          <Select
            value={filters.brand_id || "all"}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, brand_id: v === "all" ? "" : v }))
            }
          >
            <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="royalty-filter-brand">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {brandOptions.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </RoyaltyFilterField>
        <RoyaltyFilterField label="Statut">
          <Select
            value={filters.status || "all"}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))
            }
          >
            <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="royalty-filter-status">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="draft">Prévisionnel</SelectItem>
              <SelectItem value="validated">Validé</SelectItem>
            </SelectContent>
          </Select>
        </RoyaltyFilterField>
        <RoyaltyFilterField label="Recherche">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <Input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Nom du bénéficiaire…"
              className="h-8 text-xs w-[200px] pl-7"
              data-testid="royalty-filter-search"
            />
          </div>
        </RoyaltyFilterField>
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={resetFilters}
            data-testid="royalty-filter-reset"
          >
            <X size={12} className="mr-1" />
            Réinitialiser ({activeFiltersCount})
          </Button>
        )}
        <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500" data-testid="royalty-filter-count">
          {filteredItems.length} / {items.length} bénéficiaire{items.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Bénéficiaires" value={filteredItems.length} icon={<Users size={13} />} accent="#0055FF" />
        <StatCard label="CA net cumulé (période)" value={money(totals.totalCa)} icon={<Percent size={13} />} accent="#F97316" />
        <StatCard label="Total Royalties HT" value={money(totals.totalHt)} icon={<CheckCircle2 size={13} />} accent="#10B981" />
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Bénéficiaire</th>
                <th className="text-left px-4 py-2.5">Départements</th>
                <th className="text-right px-4 py-2.5">CA net</th>
                <th className="text-right px-4 py-2.5">Taux</th>
                <th className="text-right px-4 py-2.5">Total HT</th>
                <th className="text-center px-4 py-2.5">Statut</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-zinc-400 font-mono">Chargement…</td></tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-xs text-zinc-400">
                    {items.length === 0
                      ? "Aucun bénéficiaire actif pour ce mois."
                      : "Aucun résultat pour ces filtres."}
                  </td>
                </tr>
              ) : (
                filteredItems.map((r) => {
                  const isValid = r.status === "validated";
                  return (
                    <tr
                      key={r.beneficiary_id}
                      className="border-t border-zinc-100 hover:bg-zinc-50"
                      data-testid={`royalty-statement-row-${r.beneficiary_id}`}
                    >
                      <td className="px-4 py-2.5 font-medium text-zinc-900">{r.contact_name}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(r.brands || []).map((b) => (
                            <Badge
                              key={b.id}
                              variant="outline"
                              className="text-[10px]"
                              style={{ borderColor: b.color || "#e4e4e7", color: b.color || "#3f3f46" }}
                            >
                              {b.name}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{money(r.ca_net)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        {Number(r.rate || 0).toFixed(2).replace(".", ",")}&nbsp;%
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{money(r.amount_ht)}</td>
                      <td className="px-4 py-2.5 text-center">
                        {isValid ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0">
                            <ShieldCheck size={11} className="mr-1" /> Validé
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">
                            Prévisionnel
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="mr-1.5"
                          onClick={() => downloadPdf(r.beneficiary_id, r.contact_name)}
                          title="Télécharger le relevé PDF (période sélectionnée)"
                          data-testid={`royalty-pdf-${r.beneficiary_id}`}
                        >
                          <Download size={12} className="mr-1" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setDetailBid(r.beneficiary_id)}
                          title="Voir le détail"
                          className="bg-[#0055FF] hover:bg-[#0044CC]"
                          data-testid={`royalty-detail-${r.beneficiary_id}`}
                        >
                          <ChevronRight size={12} className="mr-1" /> Détail
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StatementDetailDialog
        open={!!detail}
        onClose={() => setDetailBid(null)}
        detail={detail}
        month={singleMonth || currentMonth()}
        isSingleMonth={isSingleMonth}
        monthsCount={monthsInRange.length}
        rangeLabel={
          periodLabel ||
          (range?.from && range?.to
            ? `${formatDate(range.from, "dd MMM yyyy", { locale: frLocale })} → ${formatDate(range.to, "dd MMM yyyy", { locale: frLocale })}`
            : "—")
        }
        isAdmin={isAdmin}
        onRefresh={load}
        onDownloadPdf={() => detail && downloadPdf(detail.beneficiary_id, detail.contact_name)}
      />
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white flex items-start gap-3">
      <span
        className="h-8 w-8 rounded-sm text-white inline-flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: accent }}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-lg font-semibold text-zinc-900 font-mono truncate">{value}</p>
      </div>
    </div>
  );
}

function RoyaltyFilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">{label}</span>
      {children}
    </div>
  );
}

/* ═══════════ Modale détail ═══════════ */
function StatementDetailDialog({ open, onClose, detail, month, isSingleMonth = true, monthsCount = 1, rangeLabel = "", isAdmin, onRefresh, onDownloadPdf }) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustValue, setAdjustValue] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateOpen, setValidateOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState("immediate");
  const [reopenOpen, setReopenOpen] = useState(false);
  // Bloc — Réajustement sur périodes précédentes
  const [editingAdj, setEditingAdj] = useState(false);
  const [savingAdj, setSavingAdj] = useState(false);
  const [adjType, setAdjType] = useState("manque_a_gagner");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjComment, setAdjComment] = useState("");
  // Bloc — Avance versée sur royalties
  const [editingAdv, setEditingAdv] = useState(false);
  const [savingAdv, setSavingAdv] = useState(false);
  const [advAmount, setAdvAmount] = useState("");
  const [advDate, setAdvDate] = useState("");
  const [advMethod, setAdvMethod] = useState("virement");

  useEffect(() => {
    if (detail) {
      setAdjustValue(detail.manual_adjustment_ht ?? "");
      setAdjustNote(detail.adjustment_note ?? "");
      setAdjustOpen(false);
      // Réajustement
      setAdjType(detail.adjustment_type || "manque_a_gagner");
      setAdjAmount(detail.adjustment_amount ? String(detail.adjustment_amount) : "");
      setAdjComment(detail.adjustment_comment || "");
      setEditingAdj(false);
      // Avance
      setAdvAmount(detail.advance_amount ? String(detail.advance_amount) : "");
      setAdvDate(detail.advance_date || "");
      setAdvMethod(detail.advance_method || "virement");
      setEditingAdv(false);
    }
  }, [detail]);

  if (!detail) return null;
  const isValidated = detail.status === "validated";
  // Actions de validation/correction : réservées au mode mois unique.
  const canEdit = isAdmin && isSingleMonth;

  const saveAdjust = async () => {
    setSaving(true);
    try {
      const val = adjustValue === "" || adjustValue === null ? null : Number(adjustValue);
      await api.patch(`/royalty-statements/${detail.beneficiary_id}/${month}`, {
        manual_adjustment_ht: val,
        adjustment_note: adjustNote?.trim() || null,
      });
      toast.success("Correction manuelle enregistrée");
      setAdjustOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const saveAdjustment = async () => {
    if (isValidated) return;
    const amt = adjAmount === "" ? 0 : Number(adjAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Le montant du réajustement doit être ≥ 0.");
      return;
    }
    if (amt > 0 && !adjComment.trim()) {
      toast.error("Un commentaire explicatif est requis.");
      return;
    }
    setSavingAdj(true);
    try {
      await api.patch(`/royalty-statements/${detail.beneficiary_id}/${month}`, {
        adjustment_amount: amt,
        adjustment_type: amt > 0 ? adjType : null,
        adjustment_comment: amt > 0 ? adjComment.trim() : null,
      });
      toast.success(amt > 0 ? "Réajustement enregistré" : "Réajustement retiré");
      setEditingAdj(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdj(false);
    }
  };

  const removeAdjustment = async () => {
    if (isValidated) return;
    setSavingAdj(true);
    try {
      await api.patch(`/royalty-statements/${detail.beneficiary_id}/${month}`, {
        adjustment_amount: 0,
        adjustment_type: null,
        adjustment_comment: null,
      });
      toast.success("Réajustement retiré");
      setAdjAmount("");
      setAdjComment("");
      setEditingAdj(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdj(false);
    }
  };

  const saveAdvance = async () => {
    if (isValidated) return;
    const amt = advAmount === "" ? 0 : Number(advAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Le montant de l'avance doit être ≥ 0.");
      return;
    }
    if (amt > 0 && !advDate) {
      toast.error("La date de versement est requise.");
      return;
    }
    if (amt > 0 && !advMethod) {
      toast.error("Le mode de versement est requis.");
      return;
    }
    setSavingAdv(true);
    try {
      await api.patch(`/royalty-statements/${detail.beneficiary_id}/${month}`, {
        advance_amount: amt,
        advance_date: amt > 0 ? advDate : null,
        advance_method: amt > 0 ? advMethod : null,
      });
      toast.success(amt > 0 ? "Avance versée enregistrée" : "Avance retirée");
      setEditingAdv(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdv(false);
    }
  };

  const removeAdvance = async () => {
    if (isValidated) return;
    setSavingAdv(true);
    try {
      await api.patch(`/royalty-statements/${detail.beneficiary_id}/${month}`, {
        advance_amount: 0,
        advance_date: null,
        advance_method: null,
      });
      toast.success("Avance retirée");
      setAdvAmount("");
      setAdvDate("");
      setEditingAdv(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdv(false);
    }
  };

  const validate = async () => {
    setValidating(true);
    try {
      await api.post(
        `/royalty-statements/${detail.beneficiary_id}/${month}/validate`,
        { payment_mode: paymentMode },
      );
      toast.success(
        paymentMode === "deferred"
          ? "Relevé validé — versement reporté au mois suivant"
          : "Relevé validé — versement immédiat",
      );
      setValidateOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setValidating(false);
    }
  };

  const reopen = async () => {
    setReopenOpen(false);
    try {
      await api.post(`/royalty-statements/${detail.beneficiary_id}/${month}/reopen`);
      toast.success("Relevé rouvert");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="royalty-detail-dialog">
        <DialogHeader>
          <DialogTitle>Relevé Royalties — {detail.contact_name}</DialogTitle>
          <DialogDescription>
            Période :{" "}
            {isSingleMonth ? (
              <span className="capitalize">{monthLabel(month)}</span>
            ) : (
              <span>{rangeLabel} <span className="text-zinc-500">({monthsCount} mois)</span></span>
            )}{" "}
            — Statut :{" "}
            {isSingleMonth ? (
              isValidated ? (
                <span className="text-emerald-700 font-semibold">
                  Validé {detail.validated_at ? `(${new Date(detail.validated_at).toLocaleDateString("fr-FR")})` : ""}
                  {detail.payment_mode === "deferred" && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                      data-testid="royalty-payment-mode-badge"
                    >
                      Report de versement
                    </span>
                  )}
                  {detail.payment_mode === "immediate" && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200"
                      data-testid="royalty-payment-mode-badge"
                    >
                      Versé
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-700 font-semibold">Prévisionnel</span>
              )
            ) : (
              <span className={isValidated ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                {isValidated ? "Validé (tous les mois)" : "Prévisionnel (au moins un mois non validé)"}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SectionCard title="Contrat" icon={<CalendarDays size={13} />} accent="#0055FF">
            <KV label="Départements" value={
              <span className="inline-flex flex-wrap gap-1 justify-end">
                {(detail.brands || []).map((b) => (
                  <Badge key={b.id} variant="outline" className="text-[10px]" style={{ borderColor: b.color, color: b.color }}>
                    {b.name}
                  </Badge>
                ))}
              </span>
            } />
            <KV label="Taux appliqué" value={`${Number(detail.rate || 0).toFixed(2).replace(".", ",")} %`} />
            <KV label="Contrat" value={`${frDate(detail.contract_start)} → ${frDate(detail.contract_end)}`} />
            <KV label="Période retenue" value={
              isSingleMonth ? (
                detail.period_start
                  ? `${frDate(detail.period_start)} → ${frDate(detail.period_end)}`
                  : <span className="text-zinc-400 italic">hors contrat</span>
              ) : (
                <span>{rangeLabel}</span>
              )
            } />
          </SectionCard>

          <SectionCard title={isSingleMonth ? "CA net par département" : "CA net par département (plage cumulée)"} icon={<Building2 size={13} />} accent="#F97316">
            {(detail.by_brand || []).length === 0 ? (
              <p className="col-span-2 text-xs text-zinc-500 italic">
                Aucune transaction sur la période retenue.
              </p>
            ) : (
              <div className="col-span-2 border border-zinc-200 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-1.5">Département</th>
                      <th className="text-right px-3 py-1.5">Ventes</th>
                      <th className="text-right px-3 py-1.5">Remb.</th>
                      <th className="text-right px-3 py-1.5">CA net</th>
                      <th className="text-right px-3 py-1.5">Nb tx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.by_brand || []).map((br) => (
                      <tr key={br.brand_id} className="border-t border-zinc-100">
                        <td className="px-3 py-1.5">{br.name}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{money(br.sales)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{money(br.refunds)}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-semibold">{money(br.net)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{br.count}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-zinc-200 bg-zinc-50">
                      <td className="px-3 py-1.5 font-semibold">Total</td>
                      <td className="px-3 py-1.5 text-right font-mono">{money(detail.ca_sales)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{money(detail.ca_refunds)}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{money(detail.ca_net)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{detail.tx_count}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard title={isSingleMonth ? "Calcul Royalties" : "Calcul Royalties (cumulé sur la plage)"} icon={<Percent size={13} />} accent="#10B981">
            <KV label={isSingleMonth ? "CA net retenu" : "CA net cumulé (plage)"} value={money(detail.ca_net)} />
            <KV label="Taux Royalties" value={`${Number(detail.rate || 0).toFixed(2).replace(".", ",")} %`} />
            <KV label={isSingleMonth ? "Montant calculé HT" : "Montant calculé HT cumulé"} value={money(detail.computed_amount_ht)} />
            {(detail.carried_over_ht || 0) > 0 && (
              <>
                <KV
                  label="Report reçu du mois précédent"
                  value={
                    <span className="font-mono font-semibold text-amber-700" data-testid="royalty-carried-over-value">
                      + {money(detail.carried_over_ht)}
                    </span>
                  }
                />
                {detail.carried_over_source_month && (
                  <div className="col-span-2 text-xs text-amber-700 italic bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
                    Report du relevé validé de {monthLabel(detail.carried_over_source_month)} (marqué en « Report de versement »).
                  </div>
                )}
              </>
            )}
            {detail.manual_adjustment_ht !== null && detail.manual_adjustment_ht !== undefined && (
              <>
                <KV label={isSingleMonth ? "Correction manuelle HT" : "Corrections manuelles cumulées HT"} value={money(detail.manual_adjustment_ht)} />
                {isSingleMonth && detail.adjustment_note && (
                  <div className="col-span-2 text-xs text-zinc-500 italic">
                    Motif : {detail.adjustment_note}
                  </div>
                )}
              </>
            )}
            {canEdit && (
              <div className="col-span-2 pt-2 border-t border-zinc-100 flex flex-wrap items-center gap-2">
                {!adjustOpen ? (
                  <Button size="sm" variant="outline" onClick={() => setAdjustOpen(true)} data-testid="royalty-open-adjust">
                    <Pencil size={11} className="mr-1" />
                    {detail.manual_adjustment_ht != null ? "Modifier la correction" : "Appliquer une correction"}
                  </Button>
                ) : (
                  <div className="w-full space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Montant HT corrigé (laisser vide pour annuler)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adjustValue}
                          onChange={(e) => setAdjustValue(e.target.value)}
                          placeholder="0.00"
                          data-testid="royalty-adjust-value"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Motif (optionnel)</Label>
                        <Input
                          value={adjustNote}
                          onChange={(e) => setAdjustNote(e.target.value)}
                          placeholder="Ex : régularisation transaction X"
                          data-testid="royalty-adjust-note"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveAdjust} disabled={saving} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="royalty-save-adjust">
                        <Save size={11} className="mr-1" /> {saving ? "…" : "Enregistrer"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setAdjustOpen(false); setAdjustValue(detail.manual_adjustment_ht ?? ""); setAdjustNote(detail.adjustment_note ?? ""); }}>
                        <X size={11} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {/* Bloc : Réajustement Royalties sur périodes précédentes */}
          <SectionCard title="Réajustement Royalties sur périodes précédentes" icon={<ArrowUpDown size={13} />} accent="#8B5CF6">
            <div className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
              Saisie admin. Choisissez « Manque à gagner » (ajouté au relevé) ou « Trop perçu » (soustrait), le montant en € et un motif obligatoire.
            </div>
            {canEdit && !isValidated ? (
              editingAdj ? (
                <div className="col-span-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="royalty-radio-adj-manque">
                      <input
                        type="radio"
                        name="royalty-adj-type"
                        value="manque_a_gagner"
                        checked={adjType === "manque_a_gagner"}
                        onChange={() => setAdjType("manque_a_gagner")}
                        className="accent-emerald-600"
                      />
                      <ArrowUpCircle size={13} className="text-emerald-600" />
                      Manque à gagner (+)
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="royalty-radio-adj-trop">
                      <input
                        type="radio"
                        name="royalty-adj-type"
                        value="trop_percu"
                        checked={adjType === "trop_percu"}
                        onChange={() => setAdjType("trop_percu")}
                        className="accent-rose-600"
                      />
                      <ArrowDownCircle size={13} className="text-rose-600" />
                      Trop perçu (−)
                    </label>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[140px]">
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex. 150.00"
                        value={adjAmount}
                        onChange={(e) => setAdjAmount(e.target.value)}
                        data-testid="royalty-input-adj-amount"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Motif du réajustement <span className="text-red-500">*</span></Label>
                    <textarea
                      value={adjComment}
                      onChange={(e) => setAdjComment(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Ex. Régularisation transaction remboursée à tort en janvier 2026"
                      className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/40"
                      data-testid="royalty-input-adj-comment"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1">{adjComment.length}/1000</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={saveAdjustment} disabled={savingAdj} className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="royalty-btn-save-adj">
                      <Save size={12} className="mr-1" /> {savingAdj ? "…" : "Enregistrer"}
                    </Button>
                    {(detail.adjustment_amount || 0) > 0 && (
                      <Button size="sm" variant="outline" onClick={removeAdjustment} disabled={savingAdj} data-testid="royalty-btn-remove-adj">
                        <X size={12} className="mr-1" /> Retirer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingAdj(false);
                        setAdjAmount(detail.adjustment_amount ? String(detail.adjustment_amount) : "");
                        setAdjType(detail.adjustment_type || "manque_a_gagner");
                        setAdjComment(detail.adjustment_comment || "");
                      }}
                      data-testid="royalty-btn-cancel-adj"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Réajustement</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900" data-testid="royalty-value-adj">
                      {(detail.adjustment_amount || 0) > 0
                        ? `${detail.adjustment_type === "trop_percu" ? "− " : "+ "}${money(detail.adjustment_amount)}`
                        : money(0)}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setEditingAdj(true)} data-testid="royalty-btn-edit-adj">
                      <Pencil size={12} className="mr-1" /> {(detail.adjustment_amount || 0) > 0 ? "Modifier" : "Ajouter"}
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Réajustement</span>
                <span className="font-mono text-sm text-zinc-900" data-testid="royalty-value-adj-readonly">
                  {(detail.adjustment_amount || 0) > 0
                    ? `${detail.adjustment_type === "trop_percu" ? "− " : "+ "}${money(detail.adjustment_amount)}`
                    : money(0)}
                </span>
              </div>
            )}
            {(detail.adjustment_amount || 0) > 0 && detail.adjustment_comment && !editingAdj && (
              <div className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-xs italic text-zinc-700" data-testid="royalty-value-adj-comment">
                <span className="font-semibold not-italic">Motif :</span> {detail.adjustment_comment}
              </div>
            )}
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Impact sur total HT</span>
              <span className={`font-bold ${(detail.adjustment_signed || 0) < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="royalty-detail-adj-total">
                {(detail.adjustment_signed || 0) >= 0 ? "+ " : "− "}{money(Math.abs(detail.adjustment_signed || 0))}
              </span>
            </div>
          </SectionCard>

          {/* Bloc : Avance versée sur Royalties */}
          <SectionCard title="Avance versée sur Royalties" icon={<BanknoteArrowDown size={13} />} accent="#DB2777">
            <div className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
              Saisie admin. Le montant versé au partenaire avant validation est retranché de son relevé Royalties final.
            </div>
            {canEdit && !isValidated ? (
              editingAdv ? (
                <div className="col-span-2 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Montant (€) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex. 500.00"
                        value={advAmount}
                        onChange={(e) => setAdvAmount(e.target.value)}
                        data-testid="royalty-input-adv-amount"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Date <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={advDate}
                        onChange={(e) => setAdvDate(e.target.value)}
                        data-testid="royalty-input-adv-date"
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Mode <span className="text-red-500">*</span></Label>
                      <SearchableSelect
                        value={advMethod}
                        onChange={(v) => setAdvMethod(v)}
                        options={[
                          { value: "virement", label: "Virement" },
                          { value: "especes", label: "Espèces" },
                          { value: "cheque", label: "Chèque" },
                          { value: "autre", label: "Autre" },
                        ]}
                        placeholder="Choisir"
                        searchPlaceholder="Rechercher…"
                        data-testid="royalty-input-adv-method"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={saveAdvance} disabled={savingAdv} className="bg-[#DB2777] hover:bg-[#BE185D]" data-testid="royalty-btn-save-adv">
                      <Save size={12} className="mr-1" /> {savingAdv ? "…" : "Enregistrer"}
                    </Button>
                    {(detail.advance_amount || 0) > 0 && (
                      <Button size="sm" variant="outline" onClick={removeAdvance} disabled={savingAdv} data-testid="royalty-btn-remove-adv">
                        <X size={12} className="mr-1" /> Retirer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingAdv(false);
                        setAdvAmount(detail.advance_amount ? String(detail.advance_amount) : "");
                        setAdvDate(detail.advance_date || "");
                        setAdvMethod(detail.advance_method || "virement");
                      }}
                      data-testid="royalty-btn-cancel-adv"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Avance versée</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900" data-testid="royalty-value-adv">
                      {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setEditingAdv(true)} data-testid="royalty-btn-edit-adv">
                      <Pencil size={12} className="mr-1" /> {(detail.advance_amount || 0) > 0 ? "Modifier" : "Ajouter"}
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Avance versée</span>
                <span className="font-mono text-sm text-zinc-900" data-testid="royalty-value-adv-readonly">
                  {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
                </span>
              </div>
            )}
            {(detail.advance_amount || 0) > 0 && !editingAdv && (
              <div className="col-span-2 grid grid-cols-2 gap-2 text-xs text-zinc-700" data-testid="royalty-value-adv-meta">
                <div>
                  <span className="font-mono uppercase tracking-wider text-zinc-500 text-[10px]">Date : </span>
                  <span className="font-mono">{detail.advance_date || "—"}</span>
                </div>
                <div>
                  <span className="font-mono uppercase tracking-wider text-zinc-500 text-[10px]">Mode : </span>
                  <span className="font-mono">
                    {({ virement: "Virement", especes: "Espèces", cheque: "Chèque", autre: "Autre" }[detail.advance_method]) || "—"}
                  </span>
                </div>
              </div>
            )}
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Impact sur total HT</span>
              <span className="font-bold text-rose-700" data-testid="royalty-detail-adv-total">
                {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
              </span>
            </div>
          </SectionCard>

          <div className="bg-[#F97316] text-white rounded-sm p-4 flex items-center justify-between">
            <span className="font-semibold text-sm uppercase tracking-wider">
              {isSingleMonth ? "Total Royalties HT" : "Total Royalties HT (plage)"}
            </span>
            <span className="font-mono text-2xl font-bold" data-testid="royalty-total-ht">{money(detail.amount_ht)}</span>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onDownloadPdf()} data-testid="royalty-modal-pdf">
            <Download size={13} className="mr-1" /> Télécharger PDF
          </Button>
          {canEdit && !isValidated && (
            <Button
              onClick={() => { setPaymentMode("immediate"); setValidateOpen(true); }}
              disabled={validating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="royalty-validate-btn"
            >
              <CheckCircle2 size={13} className="mr-1" />
              {validating ? "Validation…" : "Valider manuellement"}
            </Button>
          )}
          {canEdit && isValidated && (
            <Button variant="outline" onClick={() => setReopenOpen(true)} data-testid="royalty-reopen-btn">
              <RotateCcw size={13} className="mr-1" /> Rouvrir
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rouvrir ce relevé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le snapshot figé sera supprimé et le relevé sera recalculé à la volée à partir des transactions actuelles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={reopen} data-testid="royalty-reopen-confirm">
              Rouvrir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={validateOpen} onOpenChange={(v) => !validating && setValidateOpen(v)}>
        <AlertDialogContent data-testid="royalty-validate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Valider le relevé — Mode de versement</AlertDialogTitle>
            <AlertDialogDescription>
              Une fois validé, le relevé est figé. Choisissez comment le montant HT est traité :
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <label
              className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${
                paymentMode === "immediate" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid="royalty-pm-immediate-option"
            >
              <input
                type="radio"
                name="payment-mode"
                value="immediate"
                checked={paymentMode === "immediate"}
                onChange={() => setPaymentMode("immediate")}
                className="mt-1"
                data-testid="royalty-pm-immediate"
              />
              <div className="flex-1">
                <p className="font-semibold text-sm text-zinc-900">Versement immédiat</p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  Le montant HT ({money(detail.amount_ht)}) est marqué comme versé à la validation.
                </p>
              </div>
            </label>
            <label
              className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${
                paymentMode === "deferred" ? "border-amber-500 bg-amber-50" : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid="royalty-pm-deferred-option"
            >
              <input
                type="radio"
                name="payment-mode"
                value="deferred"
                checked={paymentMode === "deferred"}
                onChange={() => setPaymentMode("deferred")}
                className="mt-1"
                data-testid="royalty-pm-deferred"
              />
              <div className="flex-1">
                <p className="font-semibold text-sm text-zinc-900">Report de versement</p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  Le montant HT ({money(detail.amount_ht)}) sera reporté et additionné au relevé du mois suivant.
                </p>
              </div>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={validating}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={validate}
              disabled={validating}
              className={paymentMode === "deferred" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="royalty-validate-confirm"
            >
              {validating ? "Validation…" : (paymentMode === "deferred" ? "Valider avec report" : "Valider et payer")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function SectionCard({ title, icon, accent, children }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-7 w-7 rounded-sm text-white inline-flex items-center justify-center" style={{ backgroundColor: accent }}>
          {icon}
        </span>
        <h4 className="text-sm font-semibold text-zinc-900">{title}</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-2 py-1 items-start">
      <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{label}</span>
      <span className="font-mono text-zinc-900 text-right">{value}</span>
    </div>
  );
}
